import { useEffect, useMemo, useRef } from "react";
import { useFormikContext } from "formik";
import { Button } from "../Buttons";
import { requestAuth } from "../services/RequestService";
import { useDialog } from "../../contexts/DialogContext";
import FormDefault from "../Forms/FormDefault";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Request } from "../../hooks/Request";

function hasValue(value) {
  return value !== null && value !== undefined && value !== "";
}

function hasOwnValue(source, name) {
  return Object.prototype.hasOwnProperty.call(source || {}, name);
}

function safeJsonParse(value, fallback = {}) {
  try {
    if (!value) return fallback;
    return typeof value === "string" ? JSON.parse(value) : value;
  } catch {
    return fallback;
  }
}

function normalizeContainerType(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/^X\s*/i, "");
}

function getContainerGroup(item) {
  const parsed = safeJsonParse(item?.description2 || item?.Description2);
  const quantity =
    parsed.quantity ?? item?.numberValue ?? item?.NumberValue ?? "";
  const containerType =
    parsed.containerType ?? item?.stringValue ?? item?.StringValue ?? "";

  return {
    quantity,
    containerType: normalizeContainerType(containerType),
  };
}

function buildContainerQuantitySummary(groups) {
  if (!Array.isArray(groups)) return "";

  const quantitiesByType = groups
    .map(getContainerGroup)
    .reduce((acc, group) => {
      const quantity = Number(group.quantity);

      if (!group.containerType || !Number.isFinite(quantity) || quantity <= 0) {
        return acc;
      }

      acc.set(
        group.containerType,
        (acc.get(group.containerType) || 0) + quantity,
      );
      return acc;
    }, new Map());

  return Array.from(quantitiesByType.entries())
    .map(([containerType, quantity]) => `${quantity} X${containerType}`)
    .join(" / ");
}

function applyContainerQuantityFallback(data, containerQuantityFallback) {
  if (!containerQuantityFallback || hasValue(data.cantidad)) {
    return data;
  }

  return { ...data, cantidad: containerQuantityFallback };
}

const InputGeneralTransportData = ({ field, setValues }) => {
  const { openDialog, dialogClose } = useDialog();
  const { values } = useFormikContext();
  const initializedValuesRef = useRef(false);
  const hydratedQuantityFallbackRef = useRef(false);
  const backfilledQuantityRef = useRef(false);

  const subFields = useMemo(() => field.fieldsList || [], [field.fieldsList]);
  const quantityField = subFields.find((f) => f.name === "cantidad");
  const quantityValue = quantityField ? values[quantityField.name] : "";
  const {
    data: quantitySubData,
    loading: quantitySubDataLoaded,
    call: reloadQuantitySubData,
  } = Request({
    urlApi: quantityField
      ? `/DataSet/procedure/${field.id}/fieldname/${quantityField.name}/subData`
      : null,
  });
  const containerQuantityFallback = useMemo(
    () => buildContainerQuantitySummary(quantitySubData),
    [quantitySubData],
  );

  const quantityExistingData = useMemo(() => {
    if (!Array.isArray(quantitySubData)) {
      return null;
    }

    return quantitySubData[0] || null;
  }, [quantitySubData]);

  const savedData = useMemo(() => {
    const parsed = safeJsonParse(values[field?.name] || "{}");
    const result = parsed && typeof parsed === "object" ? { ...parsed } : {};

    subFields.forEach((f) => {
      if (hasValue(f.initial)) {
        result[f.name] = f.initial;
      }
    });

    subFields.forEach((f) => {
      if (hasOwnValue(values, f.name)) {
        result[f.name] = values[f.name];
      }
    });

    return applyContainerQuantityFallback(result, containerQuantityFallback);
  }, [values, field?.name, subFields, containerQuantityFallback]);

  useEffect(() => {
    if (initializedValuesRef.current) {
      return;
    }

    initializedValuesRef.current = true;
    const stored = safeJsonParse(values[field?.name] || "{}");
    const updates = {};

    subFields.forEach((f) => {
      if (hasOwnValue(values, f.name) && hasValue(values[f.name])) {
        return;
      }

      if (hasValue(f.initial) && f.initial !== "false") {
        updates[f.name] = f.initial;
      } else if (hasValue(stored[f.name])) {
        updates[f.name] = stored[f.name];
      }
    });

    if (Object.keys(updates).length > 0) {
      setValues((a) => ({ ...a, ...updates }));
    }
  }, [field?.name, setValues, subFields, values]);

  useEffect(() => {
    if (
      hydratedQuantityFallbackRef.current ||
      !quantityField?.name ||
      !hasValue(containerQuantityFallback) ||
      hasValue(quantityValue)
    ) {
      return;
    }

    hydratedQuantityFallbackRef.current = true;
    setValues((a) => ({
      ...a,
      [quantityField.name]: containerQuantityFallback,
    }));
  }, [containerQuantityFallback, quantityField, quantityValue, setValues]);

  useEffect(() => {
    if (
      backfilledQuantityRef.current ||
      !quantitySubDataLoaded ||
      !quantityField?.id ||
      !hasValue(containerQuantityFallback) ||
      hasValue(quantityField.initial)
    ) {
      return;
    }

    backfilledQuantityRef.current = true;
    requestAuth("post", "/dataset", [
      {
        Value: containerQuantityFallback,
        ProcedureId: field.id,
        FieldId: quantityField.id,
        name: quantityField.name,
        url: quantityField.url || "",
        type: quantityField.type || "",
      },
    ]).catch((error) => {
      backfilledQuantityRef.current = false;
      console.error("Error al sincronizar cantidad desde subData:", error);
    });
  }, [
    containerQuantityFallback,
    field.id,
    quantityField,
    quantitySubDataLoaded,
  ]);

  const displayEntries = useMemo(
    () =>
      subFields
        .filter((f) => hasValue(savedData[f.name]))
        .map((f) => ({ label: f.label, value: savedData[f.name] })),
    [subFields, savedData],
  );

  function dataIntialValues(list) {
    let aux = {};
    list?.forEach((element) => {
      aux = {
        ...aux,
        [element.name]:
          (element.initial === "false" ? false : element.initial) || "",
      };
    });
    return aux;
  }

  function buildModalInitialValues() {
    const base = dataIntialValues(subFields);
    subFields.forEach((f) => {
      if (hasOwnValue(savedData, f.name)) {
        base[f.name] = savedData[f.name];
      }
    });
    return base;
  }

  function dataFields(list) {
    let aux = {};
    list?.forEach((element) => {
      aux = {
        ...aux,
        [element.name]: element.validate
          ? Yup.mixed().required("Campo requerido")
          : null,
      };
    });
    return aux;
  }

  function handleOpenForm() {
    openDialog(
      <div className="w-full max-w-4xl">
        <FormDefault
          formClassName="grid grid-cols-2 gap-3 p-6 bg-white rounded-lg"
          tittle={
            field.labelCustom ||
            field.label ||
            "Información Operativa del Servicio de Transporte"
          }
          fields={subFields.map((value, index) => ({
            ...value,
            id: field.id,
            fieldId: value.id,
            label: value.label,
            labelCustom: (
              <>
                <span className="text-green-600">{index + 1}.</span>{" "}
                {value.label}
              </>
            ),
            name: value.name,
            validate: value.validate,
            lockEdition: value.lockEdition,
            placeholder: "Complete el campo",
            type: value.type === "fileRead" ? "file" : value.type,
            typeInput: value.type === "fileRead" ? "file" : value.type,
            initial: savedData[value.name] || value.initial || null,
            urlApi: value.url
              ? value.url.includes("/")
                ? value.url
                : `/Dropdown/${value.url}/options`
              : "",
            urlInitial: value.url,
            value: "name",
            labelOption: "name",
          }))}
          initialValues={buildModalInitialValues()}
          validationSchema={Yup.object().shape(dataFields(subFields))}
          onSubmit={async (values, resetForm) => {
            const newSavedData = {};
            let quantitySubDataUpdate = null;

            const result = subFields.map((element) => {
              const rawValue =
                values[element.name] || values[element.name] === 0
                  ? values[element.name]
                  : "";

              newSavedData[element.name] = rawValue;

              if (element.name === "cantidad" && quantityExistingData) {
                const match = rawValue.match(/^(\d+)\s+X(.+)$/);

                const quantity = match ? Number(match[1]) : 0;
                const containerType = match ? match[2] : "";

                quantitySubDataUpdate = {
                  id: quantityExistingData.id,
                  payload: {
                    Label: rawValue,
                    NumberValue: quantity,
                    StringValue: containerType,
                    Description2: JSON.stringify({
                      ...safeJsonParse(quantityExistingData.description2),
                      quantity,
                      containerType,
                      quantityLabel: rawValue,
                    }),
                    dataSetId:
                      quantityExistingData.dataSetId ??
                      quantityExistingData.DataSetId ??
                      field.dataSetId,
                  },
                };
              }

              return {
                Value: rawValue,
                ProcedureId: field.id,
                FieldId: element.id,
                name: element.name,
                url: element.url || "",
                type: element.type || "",
              };
            });
            const nextGeneralDataValue = JSON.stringify(newSavedData);
            if (field.fieldId) {
              result.push({
                Value: nextGeneralDataValue,
                ProcedureId: field.id,
                FieldId: field.fieldId,
                name: field.name,
                url: field.url || "",
                type: field.type || "",
              });
            }

            try {
              const requests = [requestAuth("post", "/dataset", result)];

              if (quantitySubDataUpdate) {
                requests.push(
                  requestAuth(
                    "put",
                    `/DataSet/procedure/subData/${quantitySubDataUpdate.id}`,
                    quantitySubDataUpdate.payload,
                  ),
                );
              }

              await Promise.all(requests);
              setValues((a) => ({
                ...a,
                [field.name]: nextGeneralDataValue,
                ...newSavedData,
              }));
              if (quantitySubDataUpdate) {
                reloadQuantitySubData();
              }
              if (typeof field.reload === "function") {
                field.reload();
              }
              dialogClose();
              resetForm();
              toast.success("Datos guardados correctamente");
            } catch (e) {
              console.error("Error al guardar datos generales:", e);
              toast.error("Error al guardar los datos");
            }
          }}
          buttonName="Guardar"
        />
      </div>,
    );
  }

  const noData = displayEntries.length === 0;

  return (
    <div>
      <div className="flex flex-col w-full">
        {noData ? (
          <div className="border border-gray-300 rounded-lg mb-2 overflow-hidden">
            <div className="bg-gray-100 px-4 py-2 flex justify-between items-center border-b border-gray-200">
              <span className="font-semibold text-sm text-gray-800">
                <span className="text-green-600">1.</span> Información operativa
                del servicio de transporte
              </span>
              <Button onClick={handleOpenForm} className="whitespace-nowrap">
                <i className="fas fa-edit mr-1"></i> Datos Generales
              </Button>
            </div>
          </div>
        ) : (
          <div className="border border-gray-300 rounded-lg mb-2 overflow-hidden">
            <div className="bg-gray-100 px-4 py-2 flex justify-between items-center border-b border-gray-200">
              <span className="font-semibold text-sm text-gray-800">
                <span className="text-green-600">1.</span> Informacion operativa
                del servicio de transporte
              </span>
              <Button onClick={handleOpenForm} className="text-sm py-1">
                <i className="fas fa-edit mr-1"></i> Editar
              </Button>
            </div>
            <div className="p-4 bg-gray-50">
              <table className="w-full text-sm">
                <tbody>
                  {displayEntries.map((entry, idx) => (
                    <tr
                      key={idx}
                      className={idx % 2 === 0 ? "bg-gray-50" : "bg-white"}
                    >
                      <td className="px-3 py-2 font-semibold text-blue-700 w-1/2 border-r border-gray-100">
                        {entry.label}
                      </td>
                      <td className="px-3 py-2 text-gray-800">{entry.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InputGeneralTransportData;
